import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import swal from 'sweetalert';
import history from '../../history';
import { getAllPartners } from '../../api/partnerApi';
import { createServiceTransaction } from '../../api/serviceTransactionApi';

const PAYMENT_METHODS = [
	{ value: 'offline_bank', label: 'Bank transfer' },
	{ value: 'offline_cash', label: 'Cash' },
	{ value: 'offline_cheque', label: 'Cheque' },
];

function vendorLabel(partner) {
	if (!partner) return 'Vendor';
	const name = [partner.first_name, partner.last_name].filter(Boolean).join(' ')
		|| partner.company_name
		|| partner.email
		|| 'Vendor';
	return partner.company_name && name !== partner.company_name
		? `${name} (${partner.company_name})`
		: name;
}

class ServiceTransactionForm extends Component {
	constructor(props) {
		super(props);
		const qs = queryString.parse(props.location?.search || '');
		this.state = {
			partners: [],
			loading: true,
			submitting: false,
			form: {
				vendor_id: qs.vendor_id ? String(qs.vendor_id) : '',
				direction: 'debit',
				amount: '',
				currency_code: 'USD',
				payment_mode: 'offline_bank',
				receipt_no: '',
				remarks: '',
			},
		};
	}

	componentDidMount() {
		getAllPartners({ per_page: 500 })
			.then((response) => {
				const data = response.data || {};
				const list = Array.isArray(data) ? data : (data.vendors || data.partners || []);
				this.setState({ partners: list, loading: false });
			})
			.catch(() => this.setState({ loading: false }));
	}

	handleChange = (field, value) => {
		this.setState((s) => ({ form: { ...s.form, [field]: value } }));
	};

	handleSubmit = (e) => {
		e.preventDefault();
		const { form } = this.state;
		const amount = Number(form.amount);
		if (!form.vendor_id) {
			swal('Select vendor', 'Choose which vendor you are paying.', 'info');
			return;
		}
		if (!amount || amount <= 0) {
			swal('Invalid amount', 'Enter an amount greater than 0.', 'info');
			return;
		}
		this.setState({ submitting: true });
		createServiceTransaction({
			vendor_id: form.vendor_id,
			direction: form.direction || 'debit',
			amount,
			currency_code: (form.currency_code || 'USD').toUpperCase(),
			payment_mode: form.payment_mode,
			receipt_no: form.receipt_no || undefined,
			remarks: form.remarks || undefined,
		})
			.then(() => {
				swal('Recorded', 'Payment saved to the vendor ledger.', 'success');
				const back = form.vendor_id
					? `/admin/service_transactions?vendor_id=${form.vendor_id}`
					: '/admin/service_transactions';
				history.push(back);
			})
			.catch((err) => {
				swal('Error', (err.response?.data?.errors || []).join(' ') || 'Could not record payment', 'error');
			})
			.finally(() => this.setState({ submitting: false }));
	};

	render() {
		const { partners, loading, submitting, form } = this.state;

		return (
			<div className="ui container py-3">
				<Link to="/admin/service_transactions" className="text-muted small">← Vendor ledger</Link>
				<h3 className="title mt-2 mb-1">Record payout</h3>
				<p className="text-muted small mb-3">
					Records a <strong>debit</strong> — money you paid to the vendor. For paying a specific trip, use{' '}
					<Link to="/admin/summaries">Vendor payouts</Link> → View trips → Record payment.
				</p>

				{loading ? (
					<p className="text-muted">Loading vendors…</p>
				) : (
					<form onSubmit={this.handleSubmit} className="card border-0 shadow-sm">
						<div className="card-body">
							<div className="form-group">
								<label htmlFor="payout-vendor">Vendor *</label>
								<select
									id="payout-vendor"
									className="form-control"
									value={form.vendor_id}
									onChange={(e) => this.handleChange('vendor_id', e.target.value)}
									required
								>
									<option value="">— Select vendor —</option>
									{partners.map((partner) => (
										<option key={partner.id} value={partner.id}>
											{vendorLabel(partner)}
										</option>
									))}
								</select>
							</div>

							<div className="form-row">
								<div className="form-group col-md-4">
									<label htmlFor="payout-currency">Currency *</label>
									<select
										id="payout-currency"
										className="form-control"
										value={form.currency_code}
										onChange={(e) => this.handleChange('currency_code', e.target.value)}
									>
										<option value="USD">USD</option>
										<option value="NPR">NPR</option>
									</select>
								</div>
								<div className="form-group col-md-8">
									<label htmlFor="payout-amount">Amount *</label>
									<input
										id="payout-amount"
										type="number"
										min="0.01"
										step={form.currency_code === 'USD' ? '0.01' : '1'}
										className="form-control"
										value={form.amount}
										onChange={(e) => this.handleChange('amount', e.target.value)}
										placeholder="Amount you paid"
										required
									/>
								</div>
							</div>

							<div className="form-group">
								<label htmlFor="payout-mode">Payment method *</label>
								<select
									id="payout-mode"
									className="form-control"
									value={form.payment_mode}
									onChange={(e) => this.handleChange('payment_mode', e.target.value)}
								>
									{PAYMENT_METHODS.map((m) => (
										<option key={m.value} value={m.value}>{m.label}</option>
									))}
								</select>
							</div>

							<div className="form-group">
								<label htmlFor="payout-receipt">Receipt / reference no.</label>
								<input
									id="payout-receipt"
									type="text"
									className="form-control"
									value={form.receipt_no}
									onChange={(e) => this.handleChange('receipt_no', e.target.value)}
									placeholder="Optional"
								/>
							</div>

							<div className="form-group mb-0">
								<label htmlFor="payout-remarks">Remarks</label>
								<input
									id="payout-remarks"
									type="text"
									className="form-control"
									value={form.remarks}
									onChange={(e) => this.handleChange('remarks', e.target.value)}
									placeholder="Optional — e.g. Bulk payout Feb"
								/>
							</div>
						</div>
						<div className="card-footer bg-white d-flex justify-content-end gap-2">
							<button type="button" className="btn btn-outline-secondary" onClick={() => history.push('/admin/service_transactions')}>
								Cancel
							</button>
							<button type="submit" className="btn btn-primary" disabled={submitting}>
								{submitting ? 'Saving…' : 'Record payout'}
							</button>
						</div>
					</form>
				)}
			</div>
		);
	}
}

export default ServiceTransactionForm;
